import React from 'react';
import { ScrollText } from 'lucide-react';
import { useZakatGlobal } from '../context/ZakatGlobalContext';
import { getRegionalHadith } from '../config/regionalHadiths';

// Elegant gold-bordered banner with Arabic, English translation, and citation.
// Used at the top of Zakat / Inheritance / Will pages to ground the tool in
// the prophetic tradition that mandates / encourages each act.
//
// Props:
//   arabic     — Arabic text (rendered right-to-left, larger size)
//   english    — Translated meaning
//   citation   — e.g. "Sahih al-Bukhari 8 · Sahih Muslim 16"
//   testid     — optional data-testid
//   useRegional (default false) — when true, overrides the above 3 props
//                  with a region-themed hadith resolved from the visitor\u2019s
//                  currently-selected country (Sub-Saharan Africa →
//                  Ummah-as-one-body; Gulf → Muslim-is-brother-of-Muslim;
//                  Balkans → love-for-thy-brother, …) AND swaps the
//                  English line for the user\u2019s current locale when a
//                  native translation is available.
export const HadithBanner = ({ arabic, english, citation, testid, useRegional = false }) => {
  const { selectedCountry, selectedLanguage } = useZakatGlobal() || {};

  // Resolve regional override into local consts (avoid mutating props,
  // which is a small but real anti-pattern).
  let arabicText  = arabic;
  let englishText = english;
  let citationText = citation;
  if (useRegional && selectedCountry?.code) {
    const reg = getRegionalHadith(selectedCountry.code);
    arabicText   = reg.arabic;
    citationText = reg.citation;
    const langCode = selectedLanguage?.code || 'en';
    englishText  = reg.local?.[langCode] || reg.english;
  }
  return (
    <div
      data-testid={testid || 'hadith-banner'}
      className="relative mb-4 rounded-2xl border border-gold-300/70 bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-50 shadow-[0_1px_2px_rgba(140,122,85,0.08),0_8px_24px_-12px_rgba(140,122,85,0.25)] overflow-hidden"
    >
      {/* Subtle corner flourishes */}
      <div className="pointer-events-none absolute -top-6 -left-6 w-24 h-24 rounded-full bg-gold-200/30 blur-2xl" aria-hidden />
      <div className="pointer-events-none absolute -bottom-8 -right-8 w-28 h-28 rounded-full bg-[#F3EFEA] blur-2xl" aria-hidden />

      <div className="relative px-4 sm:px-5 py-3">
        {/* Hadith chip */}
        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#EDF5F1] text-[#0D4733] text-[10px] font-mono uppercase tracking-[0.18em]">
          <ScrollText className="w-3 h-3" />
          Hadith
        </div>

        {/* Arabic */}
        <p
          dir="rtl"
          lang="ar"
          className="mt-2 text-[18px] sm:text-[20px] leading-[1.9] font-arabic text-[#0D1520] selection:bg-gold-200"
          style={{ fontFamily: '"Amiri", "Scheherazade New", "Noto Naskh Arabic", serif' }}
        >
          {arabicText}
        </p>

        {/* Divider */}
        <div className="mt-2 mb-1.5 h-px bg-gradient-to-r from-transparent via-gold-300/60 to-transparent" />

        {/* English translation */}
        <p className="text-[12.5px] sm:text-[13px] leading-snug text-stone-700 italic">
          &ldquo;{englishText}&rdquo;
        </p>

        {/* Citation */}
        <p className="mt-1.5 text-[10.5px] uppercase tracking-[0.16em] font-mono text-stone-500">
          — {citationText}
        </p>
      </div>
    </div>
  );
};

// Canonical hadiths used across the three core modules.
export const HADITHS = {
  zakat: {
    arabic: 'بُنِيَ الإِسْلاَمُ عَلَى خَمْسٍ: شَهَادَةِ أَنْ لاَ إِلَهَ إِلاَّ اللَّهُ وَأَنَّ مُحَمَّدًا رَسُولُ اللَّهِ، وَإِقَامِ الصَّلاَةِ، وَإِيتَاءِ الزَّكَاةِ، وَالحَجِّ، وَصَوْمِ رَمَضَانَ.',
    english: 'Islam is built upon five [pillars]: testifying that there is no god but Allah and that Muhammad is the Messenger of Allah, establishing the prayer, paying the Zakat, performing the Hajj, and fasting in Ramadan.',
    citation: 'Sahih al-Bukhari 8 · Sahih Muslim 16',
  },
  inheritance: {
    arabic: 'تَعَلَّمُوا الفَرَائِضَ وَعَلِّمُوهَا النَّاسَ، فَإِنَّهَا نِصْفُ العِلْمِ، وَهُوَ يُنْسَى، وَهُوَ أَوَّلُ شَيْءٍ يُنْزَعُ مِنْ أُمَّتِي.',
    english: 'Learn the laws of inheritance, and teach them to the people, for they are half of useful knowledge — and it is what is first forgotten and first taken away from my Ummah.',
    citation: 'Sunan Ibn Mājah 2719 · al-Mustadrak of al-Ḥākim',
  },
  will: {
    arabic: 'مَا حَقُّ امْرِئٍ مُسْلِمٍ لَهُ شَيْءٌ يُوصِي فِيهِ، يَبِيتُ لَيْلَتَيْنِ، إِلاَّ وَوَصِيَّتُهُ مَكْتُوبَةٌ عِنْدَهُ.',
    english: 'It is not permissible for any Muslim who has something to bequeath to stay for two nights without having his last will and testament written and kept ready with him.',
    citation: 'Sahih al-Bukhari 2738 · Sahih Muslim 1627',
  },
};

export default HadithBanner;
